import { useMemo, useState } from 'react';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { ChevronDownIcon } from '@heroicons/react/16/solid';
import { LANGUAGES_FLAGS, LanguageFullNameEnum, LanguageEnum } from 'utils/constants';
import LanguageItem from './LanguageItem';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'components/ui/dropdown-menu.tsx';
import { Button } from 'components/ui/button.tsx';
import { cn } from 'utils/utils.ts';

interface LanguageSelectorProps {
  hoverStyle?: string;
  mobileStyle?: string;
  className?: string;
}

const LanguageSelector = ({ hoverStyle, mobileStyle, className }: LanguageSelectorProps) => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const flagImg = useMemo(() => {
    return LANGUAGES_FLAGS[i18n.language as LanguageEnum];
  }, [i18n.language]);

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger
        render={
          <Button
            type='button'
            variant='ghost'
            className={cn(
              'group',
              hoverStyle ?? 'bg-brand-yellow hover:bg-brand-yellow/60',
              isOpen && 'bg-brand-yellow/60'
            )}
          />
        }
      >
        <div className='flex cursor-pointer items-center gap-[6px]'>
          <img src={flagImg} alt={`${i18n.language} flag`} className='h-[16px] w-[16px] rounded-full' />

          <div
            className={cn(
              'font-[MuseoSans900] uppercase tracking-[1.5px]',
              mobileStyle ?? 'text-brand-grey group-hover:text-brand-brown max-sm:text-brand-brown',
              isOpen && '!text-brand-brown',
              className
            )}
          >
            {i18n.language}
          </div>
        </div>

        <ChevronDownIcon className='size-4 fill-brand-brown' />
      </DropdownMenuTrigger>

      <DropdownMenuContent className='w-40 border border-beige bg-white ring-0' align='start'>
        <DropdownMenuGroup>
          {_.map(_.keys(LanguageFullNameEnum), (lang: LanguageEnum) => (
            <DropdownMenuItem key={lang}>
              <LanguageItem lang={lang} fullname={_.get(LanguageFullNameEnum, lang)} />
            </DropdownMenuItem>
          ))}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default LanguageSelector;
